import { inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Observable, shareReplay, tap } from 'rxjs';

// Services
import { AppService } from '../app.service';

// Modèles
import { DocArticle, DocIndex, DocSection } from 'src/app/@shared/models/documentation/documentation.types';

/** Racine des fichiers produits par `npm run docs:build:app`. */
const DOCS_ROOT = 'assets/docs';

/** Pays servis par le manuel ; le pays du dossier y est ramené, à défaut le premier. */
const COUNTRIES = ['nc', 'pf'];

/**
 * Accès au manuel utilisateur embarqué dans l'application.
 *
 * Le manuel n'est pas une ressource d'API : ce sont des fichiers statiques
 * générés au build (cf. `docs/user-manual/IN_APP.md`), d'où un service simple
 * plutôt qu'un `ServiceBase`. L'index du pays est chargé une fois et gardé ;
 * le contenu d'un article est chargé à la demande.
 */
@Injectable({
    providedIn: 'root',
})
export class DocumentationService {
    private _appService = inject(AppService);

    /**
     * Client HTTP **sans intercepteur** : le manuel est un fichier statique, pas
     * une ressource d'API. Un `HttpClient` ordinaire verrait `apiPrefixInterceptor`
     * réécrire `assets/…` en `/api/assets/…` (404), et la requête partirait avec
     * le jeton d'authentification et le contexte du dossier, pour rien.
     */
    private _http = new HttpClient(inject(HttpBackend));

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    private _articles = new Map<string, Observable<string>>();
    private _byId = new Map<string, DocArticle>();
    private _index?: DocIndex;
    private _index$?: Observable<DocIndex>;
    private _indexCountry?: string;
    private _kitLoaded = false;

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    /** Pays du dossier sélectionné, ramené aux pays servis par le manuel. */
    public get country(): string {
        const code = this._appService.selectedClient?.country?.alpha2?.toLowerCase();
        return code && COUNTRIES.includes(code) ? code : COUNTRIES[0];
    }

    /** Index déjà chargé, ou `undefined` tant que `loadIndex$()` n'a pas abouti. */
    public get index(): DocIndex | undefined {
        return this._index;
    }

    public get sections(): DocSection[] {
        return this._index?.sections ?? [];
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public article(id: string): DocArticle | undefined {
        return this._byId.get(id);
    }

    /** Articles d'une liste d'ids, dans l'ordre demandé, silencieusement privés des inconnus. */
    public articles(ids: string[]): DocArticle[] {
        return ids.map((id) => this._byId.get(id)).filter((article): article is DocArticle => !!article);
    }

    /** Articles traitant d'un écran (`<module>.<id-ecran>`), les plus pertinents d'abord. */
    public articlesOfScreen(screen: string): DocArticle[] {
        return this.articles(this._index?.screens?.[screen] ?? []);
    }

    /** Contenu HTML d'un article. Chargé une fois par article, puis servi de mémoire. */
    public loadContent$(id: string): Observable<string> {
        const cached = this._articles.get(id);
        if (cached) {
            return cached;
        }
        const content$ = this._http
            .get(`${DOCS_ROOT}/${this.country}/articles/${id}.html`, { responseType: 'text' })
            .pipe(shareReplay(1));
        this._articles.set(id, content$);
        return content$;
    }

    /**
     * Index du pays du dossier. Rechargé si le dossier sélectionné a changé de
     * pays entre-temps — un manuel calédonien ne vaut pas pour un dossier
     * polynésien.
     */
    public loadIndex$(): Observable<DocIndex> {
        const country = this.country;
        if (this._index$ && this._indexCountry === country) {
            return this._index$;
        }
        this._reset(country);
        this._index$ = this._http.get<DocIndex>(`${DOCS_ROOT}/${country}/index.json`).pipe(
            tap((index) => {
                this._index = index;
                this._byId = new Map(index.articles.map((article) => [article.id, article]));
            }),
            shareReplay(1),
        );
        return this._index$;
    }

    /**
     * Feuille de style du manuel, chargée à la demande et une seule fois : seul
     * l'affichage d'un fragment en a besoin. Elle est scopée sous `.doc-scope`
     * au build, sans quoi elle repeindrait l'application (cf. `IN_APP.md`).
     *
     * Appelée par `DocumentationContentComponent` lui-même : la fenêtre d'aide
     * s'ouvre depuis n'importe quel écran, sans passer par la documentation.
     */
    public loadKit(): void {
        if (this._kitLoaded) {
            return;
        }
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = `${DOCS_ROOT}/doc-kit.css`;
        document.head.appendChild(link);
        this._kitLoaded = true;
    }

    /**
     * Recherche lexicale sur les titres, questions, descriptions et intertitres.
     * 189 articles se filtrent en mémoire : le moteur du site public (Pagefind)
     * n'est pas embarqué (arbitrage #966).
     */
    public search(query: string): DocArticle[] {
        const terms = this._normalize(query).split(/\s+/).filter((term) => term.length > 1);
        if (!terms.length) {
            return [];
        }
        return (this._index?.articles ?? [])
            .map((article) => ({ article, score: this._score(article, terms) }))
            .filter(({ score }) => score > 0)
            .sort((a, b) => b.score - a.score)
            .map(({ article }) => article);
    }

    /** Articles d'une section, cross-listés compris — l'ordre de lecture du manuel. */
    public sectionArticles(section: DocSection): DocArticle[] {
        return this.articles([...section.articles, ...section.cross]);
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /** Minuscules sans accents : « Trésorerie » et « tresorerie » doivent se trouver. */
    private _normalize(value: string): string {
        return value
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim();
    }

    private _reset(country: string): void {
        this._articles.clear();
        this._byId.clear();
        this._index = undefined;
        this._indexCountry = country;
    }

    /**
     * Score d'un article pour des termes déjà normalisés. Le titre et la
     * question pèsent le plus : ce sont les deux formulations écrites pour être
     * reconnues par le lecteur. Un terme absent partout annule l'article.
     */
    private _score(article: DocArticle, terms: string[]): number {
        const title = this._normalize(article.title);
        const question = this._normalize(article.question ?? '');
        const description = this._normalize(article.description ?? '');
        const headings = this._normalize(article.headings.map((heading) => heading.text).join(' '));

        let total = 0;
        for (const term of terms) {
            let score = 0;
            if (title.includes(term)) {
                score += 5;
            }
            if (question.includes(term)) {
                score += 4;
            }
            if (description.includes(term)) {
                score += 2;
            }
            if (headings.includes(term)) {
                score += 2;
            }
            if (!score) {
                return 0;
            }
            total += score;
        }
        return total;
    }
}
